import {type Json} from '@packages/utilities/json';
import {
  type Qualified,
  type TypeName,
  identifier,
  Identifier,
} from '@packages/systems/wfdl-Qualified';
import * as Variable from '../Variable';
import type {ComponentName} from '../Component/Names';
import type {Type} from '../DataTypeDefinition';
import {weakMemo} from '@packages/utilities/memo';
import {emptyObject} from '@packages/utilities/fp/utils';

declare const ExpressionBrand: unique symbol;
export type ExpressionF<T> = {[ExpressionBrand]: T};

export function fromBrandedExpressionF<a>(
  x: ExpressionF<a>
): ExposedExpressionF<a> {
  return x as any;
}

function toBrandedExpressionF<a>(x: ExposedExpressionF<a>): ExpressionF<a> {
  return x as any;
}

export type TypedF<a> = {
  type: Type;
  expr: a;
};

export function _ETyped<a>(val: TypedF<a>): ExpressionF<a> {
  return toBrandedExpressionF<a>({
    type: 'Typed',
    val,
  });
}

export type ElementF<a> = {
  id: string;
  type: Qualified<ComponentName>;
  data: a;
  meta?: Record<string, unknown>;
};

// EElement is memoized because it is used by our component conversion
// utils and supports referential integrity.
// That API is documented extensively.
export const _EElement = weakMemo(<r>(val: ElementF<r>): ExpressionF<r> => {
  return toBrandedExpressionF<r>({
    type: 'Element',
    val,
  });
});

export function _EText<r>(val: string): ExpressionF<r> {
  return toBrandedExpressionF<r>({
    type: 'Text',
    val,
  });
}

export function _EBoolean<r>(val: boolean): ExpressionF<r> {
  return toBrandedExpressionF<r>({
    type: 'Boolean',
    val,
  });
}

export function _ENumber<r>(val: number): ExpressionF<r> {
  return toBrandedExpressionF<r>({
    type: 'Number',
    val,
  });
}

export function _ERecord<r>(val: {[key: string]: r}): ExpressionF<r> {
  return toBrandedExpressionF<r>({
    type: 'Record',
    val,
  });
}

type SelectF<a> = {
  from: a;
  prop: string;
};

export function _ESelect<r>(val: SelectF<r>): ExpressionF<r> {
  return toBrandedExpressionF<r>({
    type: 'Select',
    val,
  });
}

export function _EEnum<r>(val: string): ExpressionF<r> {
  return toBrandedExpressionF<r>({
    type: 'Enum',
    val,
  });
}

export type UnionF<a> = {
  tag: string;
  value: a;
};

export function _EUnion<r>(val: UnionF<r>): ExpressionF<r> {
  return toBrandedExpressionF<r>({
    type: 'Union',
    val,
  });
}

type CaseF<a> = {
  value: a;
  cases: {
    [key: string]: a;
  };
};

export function _ECase<r>(val: CaseF<r>): ExpressionF<r> {
  return toBrandedExpressionF<r>({
    type: 'Case',
    val,
  });
}

export function _EList<r>(val: Array<r>): ExpressionF<r> {
  return toBrandedExpressionF<r>({
    type: 'List',
    val,
  });
}

type CallF<a> = {
  fun: a;
  arg: a;
};

export function _ECall<r>(val: CallF<r>): ExpressionF<r> {
  return toBrandedExpressionF<r>({
    type: 'Call',
    val,
  });
}

export type ExtendF<a> = {
  record: a;
  prop: string;
  value: a;
};

export function _EExtend<r>(val: ExtendF<r>): ExpressionF<r> {
  return toBrandedExpressionF<r>({
    type: 'Extend',
    val,
  });
}

export function _EVariable<r>(val: Variable.VariableType): ExpressionF<r> {
  return toBrandedExpressionF<r>({
    type: 'Variable',
    val,
  });
}

const select = <r>(from: r, prop: string): ExpressionF<r> =>
  _ESelect<r>({from, prop});

const _dataVar = _EVariable(Variable.unqualified(identifier('data')));

export function _EData<r>(path: Array<string>): ExpressionF<r> {
  return path.reduce(select, _dataVar as any);
}

type FunctionF<a> = {
  param: Identifier;
  body: a;
};

export function _EFunction<r>(val: FunctionF<r>): ExpressionF<r> {
  return toBrandedExpressionF<r>({
    type: 'Function',
    val,
  });
}

export function _ELiteral<r>(val: {
  name: Qualified<TypeName>;
  value: Json;
}): ExpressionF<r> {
  return toBrandedExpressionF<r>({
    type: 'Literal',
    val,
  });
}

export function _EDistinct<r>(val: {
  name: Qualified<TypeName>;
  value: r;
}): ExpressionF<r> {
  return toBrandedExpressionF<r>({
    type: 'Distinct',
    val,
  });
}

const _hole: {
  type: 'Hole';
  val: Record<string, never>;
} = {type: 'Hole', val: emptyObject as Record<string, never>};

export const _EHole = <r>(): ExpressionF<r> => toBrandedExpressionF<r>(_hole);

export type LiteralExpressionType =
  | 'Boolean'
  | 'Number'
  | 'Text'
  | 'Enum'
  | 'Literal';

export type ExposedExpressionF<r> =
  | {
      type: 'Boolean';
      val: boolean;
    }
  | {
      type: 'Number';
      val: number;
    }
  | {
      type: 'Text';
      val: string;
    }
  | {
      type: 'Enum';
      val: string;
    }
  | {
      type: 'Record';
      val: {
        [key: string]: r;
      };
    }
  | {
      type: 'Select';
      val: SelectF<r>;
    }
  | {
      type: 'Union';
      val: UnionF<r>;
    }
  | {
      type: 'Case';
      val: CaseF<r>;
    }
  | {
      type: 'List';
      val: Array<r>;
    }
  | {
      type: 'Element';
      val: ElementF<r>;
    }
  | {
      type: 'Variable';
      val: Variable.VariableType;
    }
  | {
      type: 'Call';
      val: CallF<r>;
    }
  | {
      type: 'Function';
      val: FunctionF<r>;
    }
  | {
      type: 'Literal';
      val: {
        name: Qualified<TypeName>;
        value: Json;
      };
    }
  | {
      type: 'Distinct';
      val: {
        name: Qualified<TypeName>;
        value: r;
      };
    }
  | {
      type: 'Extend';
      val: ExtendF<r>;
    }
  | {
      type: 'Typed';
      val: TypedF<r>;
    }

  /**
   * A hole is a placeholder that needs to be filled before the expression can be
   * evaluated. Holes are added by the settings UI to arguments when functions are
   * selected from the dropdown.
   */
  | {
      type: 'Hole';
      val: Record<string, never>;
    };
